import { matchPath } from "react-router-dom";
import type { Params } from "react-router-dom";
import { urls } from "../../urls";
import type { WireMessage } from "./agentMemory";

export type PageEntityKind = "contract" | "v2Event" | "v1Event" | "project" | "need" | "organization";

export type PageEntity = { kind: PageEntityKind; id: string };

export type PageContext = { description: string; entity?: PageEntity };

export const PAGE_CONTEXT_PREFIX = "Brukeren ser nå på:";

type Lookups = { entityLabel: (id: string) => string | undefined };

type EntityKind = PageEntityKind;

const ENTITY_PHRASES: Record<EntityKind, { noun: string; referenceClause: string; pronoun: string }> = {
  contract: { noun: "Kontrakt", referenceClause: "«denne kontrakten»", pronoun: "denne" },
  v2Event: { noun: "V2-anskaffelse", referenceClause: "«denne anskaffelsen»", pronoun: "denne" },
  v1Event: { noun: "V1-anskaffelse", referenceClause: "«denne anskaffelsen»", pronoun: "denne" },
  project: { noun: "Prosjekt", referenceClause: "«dette prosjektet»", pronoun: "dette" },
  need: { noun: "Behov", referenceClause: "«dette behovet»", pronoun: "dette" },
  organization: { noun: "Bedrift", referenceClause: "«denne bedriften» eller «dette selskapet»", pronoun: "denne" },
};

const entityContext = (kind: EntityKind, id: string, lookups: Lookups, idLabel = "id"): PageContext => {
  const { noun, referenceClause, pronoun } = ENTITY_PHRASES[kind];
  const label = lookups.entityLabel(id);
  const subject = label ? `${noun} «${label}» (${idLabel} ${id})` : `${noun} med ${idLabel} ${id}`;
  return {
    description: `${PAGE_CONTEXT_PREFIX} ${subject}. Når brukeren sier ${referenceClause}, er det ${pronoun}.`,
    entity: { kind, id },
  };
};

const pageOnlyContext = (pageLabel: string): PageContext => ({
  description: `${PAGE_CONTEXT_PREFIX} ${pageLabel}.`,
});

type Matcher = { pattern: string; resolve: (params: Params, search: string, lookups: Lookups) => PageContext | null };

const entity = (pattern: string, kind: EntityKind, idParam: string): Matcher => ({
  pattern,
  resolve: (params, _search, lookups) => {
    const id = params[idParam];
    return id ? entityContext(kind, id, lookups) : null;
  },
});

const entityFromQuery = (pattern: string, kind: EntityKind, idParams: string[]): Matcher => ({
  pattern,
  resolve: (_params, search, lookups) => {
    const query = new URLSearchParams(search);
    const idParam = idParams.find((param) => query.get(param));
    const id = idParam ? query.get(idParam) : null;
    return id && idParam ? entityContext(kind, id, lookups, idParam) : null;
  },
});

const page = (pattern: string, pageLabel: string): Matcher => ({
  pattern,
  resolve: () => pageOnlyContext(pageLabel),
});

const none = (pattern: string): Matcher => ({ pattern, resolve: () => null });

const MATCHERS: Matcher[] = [
  none(urls.v2.assistant),
  page(urls.home, "forsiden"),
  page(urls.v2.home, "forsiden"),
  page(urls.contracts.viewAll, "kontraktsoversikten"),
  page(urls.contracts.viewDrafts, "kontraktsoversikten (utkast)"),
  page(urls.contracts.viewPublished, "kontraktsoversikten (publiserte)"),
  page(urls.contracts.search, "kontraktsøket"),
  page(urls.contracts.searchV2, "kontraktsøket"),
  page(urls.contracts.viewTasks, "oppgaveoversikten for kontrakter"),
  page(urls.contracts.settings, "kontraktsinnstillingene"),
  page(urls.myProfile.organization.settings, "innstillingene"),
  page(urls.v2.settings, "innstillingene"),
  page(urls.v2.contracts.viewAll, "kontraktsoversikten"),
  page(urls.v2.contracts.active, "kontraktsoversikten (aktive)"),
  page(urls.v2.contracts.tasks, "oppgaveoversikten for kontrakter"),
  page(urls.events.viewAll, "oversikten over anskaffelser"),
  page(urls.v2.sourcingEvents.viewAll, "oversikten over anskaffelser"),
  page(urls.projects.viewAll, "prosjektoversikten"),
  page(urls.v2.projects.viewAll, "prosjektoversikten"),
  page(urls.v2.needs.list, "behovslista"),
  page(urls.v2.organizations, "bedriftsoversikten"),
  entityFromQuery(`${urls.v2.organizationProfile.pathname}/:section`, "organization", ["entryId", "orgId"]),
  entityFromQuery(`${urls.organizations.profile.pathname}/:section`, "organization", ["entryId", "orgId"]),
  entity(urls.contracts.viewSingle.pathname, "contract", "contractId"),
  entity(urls.contracts.edit.paths.details.pathname, "contract", "contractId"),
  entity(urls.v2.contracts.view.pathname, "contract", "contractId"),
  entity(urls.v2.contracts.edit.pathname, "contract", "contractId"),
  entity(urls.v2.sourcingEvents.view.pathname, "v2Event", "eventId"),
  entity(urls.v2.sourcingEvents.protocol.pathname, "v2Event", "eventId"),
  entity(urls.v2.sourcingEvents.bidDetail.pathname, "v2Event", "eventId"),
  entity(urls.v2.sourcingEvents.participationRequestDetail.pathname, "v2Event", "eventId"),
  entity(urls.v2.sourcingEvents.bseView.pathname, "v1Event", "eventId"),
  entity(urls.v2.sourcingEvents.bseEdit.pathname, "v1Event", "eventId"),
  entity(urls.v2.sourcingEvents.edit.paths.info.pathname, "v2Event", "eventId"),
  entity(urls.events.view.pathname, "v1Event", "eventId"),
  entity(urls.events.edit.paths.intro.pathname, "v1Event", "eventId"),
  entity(urls.projects.view.pathname, "project", "projectId"),
  entity(urls.v2.projects.view.pathname, "project", "projectId"),
  entity(urls.v2.needs.view.pathname, "need", "needId"),
  entity(urls.v2.needs.edit.pathname, "need", "needId"),
];

export const resolvePageContext = (pathname: string, search: string, lookups: Lookups): PageContext | null => {
  for (const { pattern, resolve } of MATCHERS) {
    const match = matchPath(pattern, pathname);
    if (match) {
      return resolve(match.params, search, lookups);
    }
  }
  return null;
};

export const withPageContext = (messages: WireMessage[], context: PageContext | null): WireMessage[] =>
  context ? [{ role: "system", content: context.description }, ...messages] : messages;
